import { useEffect, useState } from 'react';
import { settingsApi } from '../../lib/api';
import type { SenderIdentitySetting } from '../../lib/api';

export default function MessagingTab() {
  const [setting, setSetting] = useState<SenderIdentitySetting>({ enabled: false, style: 'prefix' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    settingsApi.getAll()
      .then(({ data }) => {
        const si = data.sender_identity as SenderIdentitySetting | undefined;
        if (si) setSetting(si);
      })
      .catch(() => setError('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  async function toggle(enabled: boolean) {
    const next = { ...setting, enabled };
    setSetting(next);
    setSaving(true);
    setError(null);
    try {
      await settingsApi.update('sender_identity', next);
    } catch {
      setSetting((s) => ({ ...s, enabled: !enabled })); // revert
      setError('Failed to save — please try again');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={{ padding: 32, textAlign: 'center', color: 'var(--shell-xs)', fontSize: '0.82rem' }}>Loading…</div>;
  }

  return (
    <div style={{ padding: '20px 24px', overflowY: 'auto' }}>
      <SettingRow
        title="Show team member's name to customers"
        description="When on, outbound WhatsApp messages are signed with the sender's name (e.g. “*Sarah:* …”) so customers know who is replying. This is the org-wide default — each conversation can override it from the chat header."
        checked={setting.enabled}
        disabled={saving}
        onChange={toggle}
      />

      <p style={{ marginTop: 18, fontSize: '0.72rem', color: 'var(--shell-xs)', lineHeight: 1.5 }}>
        WhatsApp always shows your single business name as the sender, so the team member's
        name is added inside the message text. The name shown is the agent's display name in Relay.
      </p>

      {error && (
        <div style={{ marginTop: 12, fontSize: '0.78rem', color: 'var(--red)' }}>{error}</div>
      )}
    </div>
  );
}

function SettingRow({ title, description, checked, disabled, onChange }: {
  title: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, justifyContent: 'space-between' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--shell-text)' }}>{title}</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--shell-muted)', marginTop: 4, lineHeight: 1.5 }}>{description}</div>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        style={{
          flexShrink: 0, marginTop: 2,
          width: 42, height: 24, borderRadius: 12, border: 'none',
          background: checked ? 'var(--v1)' : 'rgba(255,255,255,.15)',
          cursor: disabled ? 'wait' : 'pointer', position: 'relative',
          transition: 'background .15s', opacity: disabled ? 0.7 : 1,
        }}
      >
        <span style={{
          position: 'absolute', top: 3, left: checked ? 21 : 3,
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          transition: 'left .15s',
        }} />
      </button>
    </div>
  );
}
